import {Spinner, IconButton} from '@primer/react'
import {Dialog} from '@primer/react/experimental'
import {useRef} from 'react'
import {createPortal} from 'react-dom'
import {NewMarkdownViewer} from '@github-ui/markdown-viewer/NewMarkdownViewer'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {XIcon} from '@primer/octicons-react'
import {DockedPanel, useIsDockedPanelCompact, useIsDockedPanelOpen} from '../../contexts/DockedPanelContext'
import {ssrSafeDocument} from '@github-ui/ssr-utils'
import {DOCKED_SIDE_PANEL_CONTENT_ID} from './DockedSidePanel'
import {Panel} from './Panel'

type OverviewSidePanelProps = {
  bodyHtml: SafeHTMLString
  isLoading: boolean
  isOpen: boolean
  onClose: () => void
  toggleSidesheetRef: React.RefObject<HTMLElement | null>
}

/**
 * Renders the body content for the Overview Side Panel.
 */
function OverviewPanelBody({bodyHtml, isLoading}: Pick<OverviewSidePanelProps, 'bodyHtml' | 'isLoading'>) {
  // Check if this is the empty description state by looking for the "No description provided." text
  const isEmptyDescription = bodyHtml.includes('No description provided.')

  return isLoading ? (
    <div className="d-flex flex-justify-center">
      <Spinner />
    </div>
  ) : (
    <NewMarkdownViewer verifiedHTML={bodyHtml} className={isEmptyDescription ? 'fgColor-muted' : undefined} />
  )
}

/**
 * Renders the overview side panel for pull requests.
 *
 * Content is portaled into the docked side panel's body slot. When the docked
 * panel is not available (compact mode), a Dialog sidesheet is used as fallback.
 */
export function OverviewSidePanel({bodyHtml, isLoading, isOpen, onClose, toggleSidesheetRef}: OverviewSidePanelProps) {
  const isDockedPanelOpen = useIsDockedPanelOpen(DockedPanel.Overview)
  // Get portal target — only accessed when panel is open, so SSR-safe
  const sidePanelContent = isDockedPanelOpen ? ssrSafeDocument?.getElementById(DOCKED_SIDE_PANEL_CONTENT_ID) : null

  const closeButtonRef = useRef<HTMLButtonElement>(null)

  const isCompact = useIsDockedPanelCompact()

  if (!isOpen) return null

  if (sidePanelContent && !isCompact) {
    return createPortal(
      <Panel panelName={DockedPanel.Overview}>
        <Panel.Body>
          <OverviewPanelBody bodyHtml={bodyHtml} isLoading={isLoading} />
        </Panel.Body>
      </Panel>,
      sidePanelContent,
    )
  }

  return (
    <Dialog
      initialFocusRef={closeButtonRef}
      position={{narrow: 'fullscreen', regular: 'right', wide: 'right'}}
      returnFocusRef={toggleSidesheetRef}
      onClose={onClose}
      renderHeader={({dialogLabelId}) => (
        <Dialog.Header>
          <div className="d-flex flex-row flex-justify-between flex-items-center width-full pl-2">
            <Dialog.Title id={dialogLabelId}>Overview</Dialog.Title>
            <IconButton
              ref={closeButtonRef}
              aria-label="Close overview panel"
              icon={XIcon}
              variant="invisible"
              onClick={onClose}
            />
          </div>
        </Dialog.Header>
      )}
    >
      <OverviewPanelBody bodyHtml={bodyHtml} isLoading={isLoading} />
    </Dialog>
  )
}
